/**
 ** This service is used to simulate an api call to get table data
 */

import { Injectable } from '@angular/core';
import { defer, delay, Observable, of } from 'rxjs';

export type ActionAlert = 'attention' | 'none';

export type EmployeeStatus =
  | 'Unassigned'
  | 'Referral'
  | 'Under Invest'
  | 'Rev In Progress'
  | 'Monitor'
  | 'Terminated'
  | 'Suspended'
  | 'Requires Review';

export type EmployeeRole = 'CRNA' | 'Nurse' | 'Pharm Tec' | 'Pharmacist';

export type EmployeeRow = {
  [key: string]: string | number | ActionAlert | EmployeeStatus | EmployeeRole;
  id: string;
  actionAlert: ActionAlert;
  employeeStatus: EmployeeStatus;
  employeeName: string;
  employeeId: string;
  role: EmployeeRole;
  newVar: number;
  openVar: number;
  reviewed: number;
  highRiskMedVar: number;
};

export type SortOrder = 1 | -1;

/**
 * Local copy of the table query shape.
 *
 * NOTE: Core must remain dependency-free (no imports from shared/features),
 * so we keep this type structurally compatible with `shared/generic-table`.
 */
export interface TableQueryLike {
  page: number;
  rows: number;
  sortField?: string;
  sortOrder?: SortOrder;
  search?: string;
}

export interface TableDataResponse<T> {
  readonly data: T[];
  readonly totalRecords: number;
}

@Injectable({ providedIn: 'root' })
export class TableExampleService {
  private readonly allRows: readonly EmployeeRow[] = generateDemoEmployees(200);

  /**
   * Simulates an API endpoint doing server-side search + sort + pagination.
   */
  getData(query: TableQueryLike): Observable<TableDataResponse<EmployeeRow>> {
    return defer(() => {
      const normalized = normalizeQuery(query);

      const filtered = this.applySearch(this.allRows, normalized.search);
      const sorted = this.applySort(filtered, normalized.sortField, normalized.sortOrder);

      const start = normalized.page * normalized.rows;
      const pageData = sorted.slice(start, start + normalized.rows);

      return of({
        data: pageData,
        totalRecords: sorted.length,
      } satisfies TableDataResponse<EmployeeRow>);
    }).pipe(delay(350));
  }

  private applySearch(rows: readonly EmployeeRow[], search: string | undefined): EmployeeRow[] {
    const term = (search ?? '').trim().toLowerCase();
    if (!term) return [...rows];

    return rows.filter((r) => {
      return (
        r.employeeName.toLowerCase().includes(term) ||
        r.employeeId.toLowerCase().includes(term) ||
        r.role.toLowerCase().includes(term) ||
        r.employeeStatus.toLowerCase().includes(term)
      );
    });
  }

  private applySort(
    rows: EmployeeRow[],
    sortField: string | undefined,
    sortOrder: SortOrder | undefined
  ): EmployeeRow[] {
    if (!sortField) return rows;

    const order: SortOrder = sortOrder === -1 ? -1 : 1;

    return [...rows].sort((a, b) => {
      const av = a[sortField];
      const bv = b[sortField];

      if (av == null && bv == null) return 0;
      if (av == null) return -1 * order;
      if (bv == null) return 1 * order;

      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * order;

      return String(av).localeCompare(String(bv)) * order;
    });
  }
}

function normalizeQuery(
  q: TableQueryLike
): Required<Pick<TableQueryLike, 'page' | 'rows'>> & TableQueryLike {
  return {
    ...q,
    page: Math.max(0, q.page ?? 0),
    rows: Math.max(1, q.rows ?? 10),
  };
}

// Demo data generator
function generateDemoEmployees(count: number): EmployeeRow[] {
  const firstNames = [
    'John',
    'Davis',
    'Dulce',
    'Talan',
    'Ahmad',
    'Randy',
    'Nolan',
    'Leo',
    'Lincoln',
    'Haylie',
    'Alfonso',
    'Maria',
    'Carlos',
    'Elena',
    'James',
  ];
  const lastNames = [
    'Radcliff',
    'Franci',
    'Madsen',
    'Arcand',
    'Geidt',
    'Bergson',
    'Carder',
    'Kenter',
    'Smith',
    'Johnson',
    'Williams',
    'Brown',
    'Jones',
    'Garcia',
    'Miller',
  ];
  const statuses: EmployeeStatus[] = [
    'Unassigned',
    'Referral',
    'Under Invest',
    'Rev In Progress',
    'Monitor',
    'Terminated',
    'Suspended',
    'Requires Review',
  ];
  const roles: EmployeeRole[] = ['CRNA', 'Nurse', 'Pharm Tec', 'Pharmacist'];
  const idPrefixes = ['JHR', 'AJB', 'CLD'];

  return Array.from({ length: count }, (_, i) => {
    const firstName = firstNames[i % firstNames.length];
    const lastName = lastNames[(i * 3) % lastNames.length];
    const status = statuses[i % statuses.length];
    const role = roles[i % roles.length];
    const prefix = idPrefixes[i % idPrefixes.length];
    const idNum = 10 + (i % 20);

    // Simulate attention required for certain statuses
    const needsAttention =
      status === 'Requires Review' ||
      status === 'Rev In Progress' ||
      (status === 'Under Invest' && i % 3 === 0);

    return {
      id: String(1000 + i),
      actionAlert: needsAttention ? 'attention' : 'none',
      employeeStatus: status,
      employeeName: `${firstName} ${lastName}`,
      employeeId: `${prefix}${idNum}`,
      role,
      newVar: (i % 5) + 1,
      openVar: (i % 10) + 3,
      reviewed: (i % 8) + 1,
      highRiskMedVar: (i % 12) + 5,
    } satisfies EmployeeRow;
  });
}
